import { UserConfiguration } from "@app/core/user/domain/user-configuration";
import { Observable, catchError, of, switchMap } from "rxjs";
import { UserConfigurationRepo } from "@app/core/user/domain/repositories/user-configuration.repo";
import { GetUserConfigurationWS } from "@app/core/user/data/network/get-user-configuration.ws";
import { PostUserConfigurationWS } from "@app/core/user/data/network/post-user-configuration.ws";
import { UserConfigurationDao } from "@app/core/user/data/local/user-configuration.dao";
import { UserConfigurationDBMapper } from "@app/core/user/data/local/user-configuration-db.mapper";
import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";
import { UserConfigurationMapper } from "@app/core/user/data/network/user-configuration-ws.mapper";

/**
 * User configuration repository implementation.
 */
@Injectable({
  providedIn: "root",
})
export class UserConfigurationDataRepo implements UserConfigurationRepo {
  constructor(
    private getUserConfigurationWS: GetUserConfigurationWS,
    private postUserConfigurationWS: PostUserConfigurationWS,
    private userConfigurationDao: UserConfigurationDao
  ) {}

  get(token: string): Observable<UserConfiguration | null> {
    return this.getUserConfigurationWS.execute(token).pipe(
      map((response) => UserConfigurationMapper.deserialize(response)),
      switchMap((userConfiguration) => {
        // set the user configuration in the local storage
        this.userConfigurationDao.set(
          UserConfigurationDBMapper.fromEntity(userConfiguration)
        );
        return of(userConfiguration);
      }),
      catchError(() => {
        // return the user configuration from the local storage
        const userConfigurationDB = this.userConfigurationDao.get();
        if (userConfigurationDB == null) {
          return of(null);
        }
        return of(UserConfigurationDBMapper.toEntity(userConfigurationDB));
      })
    );
  }

  upsert(params: {
    token: string;
    userConfiguration: UserConfiguration;
  }): Observable<UserConfiguration> {
    return this.postUserConfigurationWS
      .execute({
        token: params.token,
        userConfiguration: UserConfigurationMapper.serialize(
          params.userConfiguration
        ),
      })
      .pipe(
        map((response) => UserConfigurationMapper.deserialize(response)),
        switchMap((userConfiguration) => {
          this.userConfigurationDao.set(
            UserConfigurationDBMapper.fromEntity(userConfiguration)
          );
          return of(userConfiguration);
        }),
        catchError((error) => {
          const userConfigurationDB = this.userConfigurationDao.get();
          if (!userConfigurationDB) {
            throw error;
          }
          return of(UserConfigurationDBMapper.toEntity(userConfigurationDB));
        })
      );
  }

  delete(): Observable<void> {
    this.userConfigurationDao.delete();
    return of(undefined);
  }
}
